/**
 * Stored `summary` text for every action whose wording never depends on its
 * own details. `report.updated` and `theme.updated` are excluded: their
 * summaries come from {@link describeOperations} and
 * {@link describeThemeUpdate}, not a fixed string.
 */
export const ACTIVITY_SUMMARIES = {
    'report.created': 'Created the report',
    'report.restored': 'Restored an earlier version',
    'report.refreshed': 'Switched to newer data',
    'report.deleted': 'Deleted the report',
    'dataset.created': 'Added a data source',
    'snapshot.submitted': 'Uploaded new data',
    'report.published': 'Published a link',
    'report.republished': 'Updated the published version',
    'report.unpublished': 'Unpublished the link',
    'report.remixed': 'Remixed from a published report',
    'workspace.plan_changed': 'Changed the plan',
    'source.connected': 'Connected Google',
    'source.disconnected': 'Disconnected Google',
    'source.needs_reconnect': 'Google needs reconnecting',
    'dataset.linked': 'Linked a Google Sheet',
    'dataset.unlinked': 'Unlinked the Google Sheet',
    'dataset.link_unavailable': 'The linked Sheet became unavailable',
    'workspace.deleted': 'Deleted the workspace',
    'workspace.restored': 'Restored the workspace',
    'workspace.purged': 'Workspace purged'
};
/** Plain-word name for each theme setting; a new theme key fails the build
 *  here until it has user-facing wording. */
const THEME_FIELD_LABELS = {
    typePair: 'font',
    cover: 'cover',
    blockNumbers: 'block numbers',
    footerText: 'footer',
    preparedBy: 'Prepared by',
    closingNote: 'closing note',
    locale: 'locale'
};
/** Summary for a `theme.updated` event, naming the changed settings. */
export function describeThemeUpdate(fields) {
    return `Changed the report theme: ${fields.map((f) => THEME_FIELD_LABELS[f]).join(', ')}`;
}
/** A widget's title, or null when neither `before` nor the batch names it. */
function widgetTitle(titles, widgetId) {
    return titles.get(widgetId) ?? null;
}
/**
 * Plain-word name for every `update_widget` patch field except `title`,
 * which gets its own "Renamed 'X' to 'Y'" phrase below. Keyed off
 * `WidgetPatch` itself (minus `title`) so a new patch field fails the build
 * here until it has user-facing wording — never a raw property name like
 * `maxCategories` leaking onto the screen.
 */
const PATCH_FIELD_LABELS = {
    caption: 'caption',
    metric: 'metric',
    format: 'number format',
    chartType: 'chart type',
    dimensionColumnId: 'grouping',
    series: 'series',
    stacked: 'stacking',
    maxCategories: 'category limit',
    columns: 'columns',
    sort: 'sort order',
    pageSize: 'page size',
    groupBy: 'grouping',
    defaultExpandedDepth: 'expanded rows',
    body: 'text'
};
/** One phrase naming what an `update_widget` patch changed, other than the
 *  title (which `phraseForOperation` handles separately). Falls back to the
 *  bare `Changed 'X'` only when the patch — impossible today — names
 *  nothing. */
function describePatch(title, patch) {
    const fields = Object.keys(patch).filter((key) => key !== 'title' && patch[key] !== undefined);
    if (fields.length === 0) {
        return `Changed '${title}'`;
    }
    if (fields.length === 1) {
        return `Changed the ${PATCH_FIELD_LABELS[fields[0]]} of '${title}'`;
    }
    return `Changed '${title}' (${fields.length} settings)`;
}
/** Whether one layout item moved, resized, both, or neither, versus its
 *  placement in `before`. A widget with no prior placement (e.g. it was
 *  added earlier in the same batch, so there is nothing to diff against) is
 *  reported as both, matching {@link describeSetLayout}'s multi-widget
 *  fallback rather than a claim we cannot support. */
function classifyLayoutChange(prior, item) {
    if (!prior) {
        return { moved: true, resized: true };
    }
    const moved = prior.x !== item.x || prior.y !== item.y;
    const resized = prior.w !== item.w || prior.h !== item.h;
    return moved || resized ? { moved, resized } : null;
}
/**
 * One phrase for a `set_layout` operation. The web grid resends every
 * widget's placement on every drag, so `items.length` is never the number of
 * widgets that actually moved — this compares each item against its
 * placement in `before` and counts only the ones that changed.
 */
function describeSetLayout(before, titles, operation) {
    const priorById = new Map(before.layout.items.map((item) => [item.widgetId, item]));
    const changes = operation.items
        .map((item) => ({ widgetId: item.widgetId, change: classifyLayoutChange(priorById.get(item.widgetId), item) }))
        .filter((entry) => entry.change !== null);
    if (changes.length === 0) {
        return 'Rearranged the layout';
    }
    if (changes.length === 1) {
        const { widgetId, change } = changes[0];
        const title = widgetTitle(titles, widgetId) ?? 'a widget';
        if (change.moved && change.resized)
            return `Moved and resized '${title}'`;
        return change.resized ? `Resized '${title}'` : `Moved '${title}'`;
    }
    const allMovedOnly = changes.every(({ change }) => change.moved && !change.resized);
    const allResizedOnly = changes.every(({ change }) => change.resized && !change.moved);
    // changes.length >= 2 here — the single-widget case returned above.
    const verb = allMovedOnly ? 'Moved' : allResizedOnly ? 'Resized' : 'Rearranged';
    return `${verb} ${changes.length} widgets`;
}
/**
 * One human phrase for one operation. Titles come from `titles`: the
 * definition as it stood before the whole batch, so a rename always reads
 * "Renamed 'Revenue' to 'Net revenue'", plus the titles of widgets the batch
 * adds (see {@link batchTitles}). `before` supplies prior layout placements.
 */
function phraseForOperation(before, titles, operation) {
    switch (operation.op) {
        case 'rename_report':
            return `Renamed the report to "${operation.title}"`;
        case 'set_report_details':
            return 'Changed the report details';
        case 'add_widget':
            return `Added '${operation.widget.title}'`;
        case 'update_widget': {
            const title = widgetTitle(titles, operation.widgetId) ?? 'a widget';
            return operation.patch.title !== undefined
                ? `Renamed '${title}' to '${operation.patch.title}'`
                : describePatch(title, operation.patch);
        }
        case 'remove_widget':
            return `Removed '${widgetTitle(titles, operation.widgetId) ?? 'a widget'}'`;
        case 'duplicate_widget':
            return `Duplicated '${widgetTitle(titles, operation.widgetId) ?? 'a widget'}'`;
        case 'set_layout':
            return describeSetLayout(before, titles, operation);
        case 'set_print_options':
            return 'Changed print options';
        case 'place_row':
            return describePlaceRow(titles, operation);
    }
}
/** `Placed 'A' at the top`, `Placed 'A' and 'B' side by side below 'C'`, or
 *  `Placed 'A', 'B' and 'C' side by side at the bottom`. */
function describePlaceRow(titles, operation) {
    const quoted = (id) => `'${widgetTitle(titles, id) ?? 'a widget'}'`;
    const names = operation.widgetIds.map(quoted);
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)} side by side`;
    const { at } = operation;
    const where = at === 'top'
        ? 'at the top'
        : at === 'bottom'
            ? 'at the bottom'
            : 'below' in at
                ? `below ${quoted(at.below)}`
                : `above ${quoted(at.above)}`;
    return `Placed ${list} ${where}`;
}
/**
 * Lowercases only the clause's first letter, so it reads correctly
 * mid-sentence — never touching quoted user content or proper nouns further
 * into the clause, which always start after that first letter.
 */
function lowercaseFirstLetter(phrase) {
    return phrase.charAt(0).toLowerCase() + phrase.slice(1);
}
/**
 * `before`'s titles plus one for each widget the batch adds or copies under an
 * id `before` lacks, so "add a widget, then place or edit it" names it instead
 * of saying 'a widget'. Widgets already in `before` keep their `before` title,
 * so a rename still reads from the old name.
 */
function batchTitles(before, operations) {
    const titles = new Map(before.widgets.map((widget) => [widget.id, widget.title]));
    for (const operation of operations) {
        // An add_widget with no id gets one minted later, so nothing in the batch can refer to it.
        if (operation.op === 'add_widget' && operation.widget.id !== undefined && !titles.has(operation.widget.id)) {
            titles.set(operation.widget.id, operation.widget.title);
        }
        else if (operation.op === 'duplicate_widget' && operation.newWidgetId !== undefined && !titles.has(operation.newWidgetId)) {
            const original = titles.get(operation.widgetId);
            if (original !== undefined)
                titles.set(operation.newWidgetId, original);
        }
    }
    return titles;
}
/**
 * One phrase per distinct change in `operations`, reading widget titles from
 * `before` and from widgets the batch itself adds. Every phrase after the first is lowercased at its first letter
 * only, since it no longer opens the sentence. More than two phrases
 * collapse to the first two plus "and N more changes" (N counts the
 * remaining phrases), so a long autosave batch still reads as one line.
 */
export function describeOperations(before, operations) {
    const titles = batchTitles(before, operations);
    const phrases = operations
        .map((operation) => phraseForOperation(before, titles, operation))
        .map((phrase, index) => (index === 0 ? phrase : lowercaseFirstLetter(phrase)));
    if (phrases.length <= 2) {
        return phrases.join(' and ');
    }
    const remaining = phrases.length - 2;
    return `${phrases.slice(0, 2).join(', ')} and ${remaining} more change${remaining === 1 ? '' : 's'}`;
}
/**
 * Every op name ever stored in a persisted revision `summary`, from the era
 * before this module existed when `applyOperations` stored the raw `op`
 * literals joined with `', '`.
 *
 * Append-only: current `OPERATION_NAMES` in `operations.ts` reflects the
 * *live* schema and may lose an entry if an op is renamed or removed, but
 * old rows in the database still contain that entry's literal string
 * forever. Adding a new op here when it ships is fine and expected; removing
 * or renaming an existing entry — even to match `OPERATION_NAMES` — is not,
 * because it would make `describeLegacySummary` stop recognising real
 * historical rows and leak their raw snake_case op name to users.
 */
export const LEGACY_SUMMARY_OPERATION_NAMES = [
    'rename_report',
    'set_report_details',
    'add_widget',
    'update_widget',
    'remove_widget',
    'duplicate_widget',
    'set_layout',
    'set_print_options',
    'place_row'
];
/**
 * Translates a stored legacy summary to fixed wording: the literal
 * `create_report`, or a `', '`-join of one or more {@link ReportOperation}
 * `op` literals (the shape `applyOperations` used to store before this
 * module existed). Any other string — including output already produced by
 * `describeOperations`, or free text — passes through unchanged.
 */
export function describeLegacySummary(summary) {
    if (summary === 'create_report') {
        return 'Created the report';
    }
    const opNames = LEGACY_SUMMARY_OPERATION_NAMES;
    const tokens = summary.split(', ');
    const isOperationJoin = tokens.every((token) => opNames.includes(token));
    return isOperationJoin ? 'Edited widgets and layout' : summary;
}
//# sourceMappingURL=activity-text.js.map